import { useState } from 'react';
import { api, ApiError } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { GooglePlaceCandidate } from '../types/admin-garage-edit';

interface LinkGoogleListingDialogProps {
  garageId: string;
  garageName: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Search Google, pick the match, confirm it (Spec 058).
 *
 * Two steps on purpose. A wrong match publishes another business's reviews on this garage's card to
 * every customer, so the confirm step names the business and address the admin is about to attach —
 * the same reasoning as the typed-confirm on bank details, one notch softer.
 */
export default function LinkGoogleListingDialog({
  garageId,
  garageName,
  onClose,
  onSaved,
}: LinkGoogleListingDialogProps) {
  const { token } = useAuth();
  const [query, setQuery] = useState(garageName);
  const [mapsUrl, setMapsUrl] = useState('');
  const [candidates, setCandidates] = useState<GooglePlaceCandidate[] | null>(null);
  const [selected, setSelected] = useState<GooglePlaceCandidate | null>(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !query.trim()) return;
    setSearching(true);
    setError(null);
    setCandidates(null);
    try {
      const result = await api.adminGarageSearchGoogleListing(garageId, query.trim(), token);
      setCandidates(result.candidates);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? 'Google search is unavailable right now. Try again in a moment.'
          : err instanceof Error ? err.message : 'Google search failed',
      );
    } finally {
      setSearching(false);
    }
  };

  const link = async (body: { placeId?: string; mapsUrl?: string }) => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await api.adminGarageLinkGoogleListing(garageId, body, token);
      onSaved();
      onClose();
    } catch (err) {
      // 409 names the other garage, 422 means Google does not recognise it, 400 carries the
      // suggested search term for a Maps URL with no place id. All three are worth showing verbatim.
      setError(err instanceof Error ? err.message : 'Could not link this listing');
      setSelected(null);
    } finally {
      setSaving(false);
    }
  };

  // ── Confirm step ─────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="admin-modal-overlay" role="dialog" aria-modal="true">
        <div className="admin-modal">
          <div className="admin-modal-header"><h3>Confirm Google listing</h3></div>
          <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
              This Google rating will appear on <strong>{garageName}</strong>’s card for every customer.
              Check it is the right business.
            </p>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem' }}>
              <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{selected.name}</div>
              <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selected.address}</div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                {selected.rating != null
                  ? <>★ {selected.rating.toFixed(1)} ({selected.ratingCount ?? 0} Google ratings)</>
                  : <span style={{ color: '#d97706' }}>No Google ratings yet — nothing will show on the card</span>}
              </div>
              {selected.distanceKm != null && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {selected.distanceKm.toFixed(2)} km from the garage’s saved location
                </div>
              )}
              {selected.businessStatus === 'CLOSED_PERMANENTLY' && (
                <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                  Google reports this business as permanently closed.
                </div>
              )}
            </div>
            {error && <div className="admin-error">{error}</div>}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={() => setSelected(null)} disabled={saving}>Back</button>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={saving}
              onClick={() => link({ placeId: selected.placeId })}
            >
              {saving ? 'Linking…' : 'Confirm link'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Search step ──────────────────────────────────────────────────────────
  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <div className="admin-modal">
        <div className="admin-modal-header"><h3>Link Google listing</h3></div>
        <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <form onSubmit={runSearch} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <label style={{ flex: 1 }}>Business name
              <input
                className="admin-input"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setError(null); }}
                placeholder="Garage name"
              />
            </label>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={searching || !query.trim()}>
              {searching ? 'Searching…' : 'Search Google'}
            </button>
          </form>

          {candidates != null && candidates.length === 0 && (
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              No Google listings found near this garage. Try a different name.
            </div>
          )}

          {candidates != null && candidates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {candidates.map((candidate) => {
                const taken = candidate.alreadyLinkedToGarageName != null;
                return (
                  <button
                    key={candidate.placeId}
                    type="button"
                    disabled={taken}
                    onClick={() => setSelected(candidate)}
                    style={{
                      textAlign: 'left',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '0.625rem 0.75rem',
                      background: taken ? '#f5f5f5' : '#fff',
                      cursor: taken ? 'not-allowed' : 'pointer',
                      opacity: taken ? 0.7 : 1,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.875rem' }}>{candidate.name}</div>
                    <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{candidate.address}</div>
                    <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {candidate.rating != null
                        ? `★ ${candidate.rating.toFixed(1)} (${candidate.ratingCount ?? 0})`
                        : 'No ratings'}
                      {candidate.distanceKm != null && ` · ${candidate.distanceKm.toFixed(2)} km away`}
                    </div>
                    {taken && (
                      <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.25rem' }}>
                        Already linked to {candidate.alreadyLinkedToGarageName}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <details>
            <summary style={{ fontSize: '0.8125rem', color: '#6b7280', cursor: 'pointer' }}>
              Paste a Google Maps link instead
            </summary>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.5rem' }}>
              <label style={{ flex: 1 }}>Google Maps URL
                <input
                  className="admin-input"
                  value={mapsUrl}
                  onChange={(e) => { setMapsUrl(e.target.value); setError(null); }}
                  placeholder="https://www.google.com/maps/..."
                />
              </label>
              <button
                type="button"
                className="admin-btn"
                disabled={saving || !mapsUrl.trim()}
                onClick={() => link({ mapsUrl: mapsUrl.trim() })}
              >
                {saving ? 'Linking…' : 'Use link'}
              </button>
            </div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.375rem 0 0' }}>
              Only some Google Maps links contain a place id. If this one does not, the search above is
              the reliable way.
            </p>
          </details>

          {error && <div className="admin-error">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
